// /settings — パスワード変更カード(セルフサービス、2026-09-19)
//
// 安全契約:
// - tid / uid は JWT cookie から backend 側で自動取得(frontend は送らない)
// - フロントで「現在のパスワード」「新しいパスワード」「新しいパスワード(確認)」の 3 フィールド
// - new_password == new_password_confirm をクライアントでも確認(UX)
// - backend がさらに verify_password(現在) → hash_password(新) + token_version += 1
// - 成功后 backend が新 cookies を返す(set_auth_cookies でブラウザに保存)
// - 別デバイスの access_token は全部失効、当前 session は新 tv で無感継続
//
// 配置:
// - SettingsPage に挿入。system_admin 専用ではない — ログインしている全 user 対象。
// - Card スタイル:plain `<Card>` + `.settings-card-head` pattern(SessionsPanel と同型)
// - pick() で ja / en 両対応。

import { useState } from "react";
import { KeyRound, Eye, EyeOff, AlertCircle, CheckCircle2 } from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { authApi } from "../../api/auth";
import { ApiError } from "../../api/client";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";

export function PasswordChangeCard() {
  const { pick } = useUiLanguage();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Client-side mismatch check(サーバ側でも検証するが UX 向上のため)
  const passwordsMatch = newPassword === newPasswordConfirm;
  const newPasswordValid = newPassword.length >= 8;
  const differentFromCurrent = newPassword !== currentPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    newPasswordConfirm.length > 0 &&
    passwordsMatch &&
    newPasswordValid &&
    differentFromCurrent &&
    !submitting;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);
    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
      // 成功:フォームをクリア + 成功バナー表示
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setSuccess(true);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : pick(
              "パスワード変更に失敗しました",
              "Failed to change password",
            );
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <div className="settings-card-head">
        <div className="settings-card-title-row">
          <div className="settings-card-icon">
            <KeyRound size={20} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              {pick("パスワード変更", "Change Password")}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {pick(
                "現在のパスワードと新しいパスワードを入力してください。他のデバイスのセッションは全てログアウトされます。",
                "Enter your current password and a new password. All other device sessions will be signed out.",
              )}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="rf-admin-form" style={{ marginTop: 12 }}>
        {/* 現在のパスワード */}
        <div className="field">
          <label className="field-label" htmlFor="pw-current">
            {pick("現在のパスワード", "Current password")}{" "}
            <span className="field-label-required">*</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              id="pw-current"
              type={showCurrent ? "text" : "password"}
              className="input"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setSuccess(false);
              }}
              required
              autoComplete="current-password"
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              aria-label={showCurrent ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                padding: 4,
              }}
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {/* 新しいパスワード */}
        <div className="field">
          <label className="field-label" htmlFor="pw-new">
            {pick("新しいパスワード", "New password")}{" "}
            <span className="field-label-required">*</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              id="pw-new"
              type={showNew ? "text" : "password"}
              className="input"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setSuccess(false);
              }}
              required
              minLength={8}
              autoComplete="new-password"
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              aria-label={showNew ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#6b7280",
                padding: 4,
              }}
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {newPassword.length > 0 && !newPasswordValid && (
            <div className="rf-field-error">
              {pick(
                "パスワードは 8 文字以上で入力してください",
                "Password must be at least 8 characters",
              )}
            </div>
          )}
        </div>

        {/* 新しいパスワード(確認) */}
        <div className="field">
          <label className="field-label" htmlFor="pw-new-confirm">
            {pick("新しいパスワード(確認)", "New password (confirm)")}{" "}
            <span className="field-label-required">*</span>
          </label>
          <input
            id="pw-new-confirm"
            type={showNew ? "text" : "password"}
            className="input"
            value={newPasswordConfirm}
            onChange={(e) => {
              setNewPasswordConfirm(e.target.value);
              setSuccess(false);
            }}
            required
            minLength={8}
            autoComplete="new-password"
          />
          {newPasswordConfirm.length > 0 && !passwordsMatch && (
            <div className="rf-field-error">
              {pick(
                "新しいパスワードが一致しません",
                "New passwords do not match",
              )}
            </div>
          )}
          {newPasswordConfirm.length > 0 && passwordsMatch && !differentFromCurrent && (
            <div className="rf-field-error">
              {pick(
                "新しいパスワードは現在のパスワードと異なる必要があります",
                "New password must differ from current password",
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="login-error" style={{ marginTop: 8 }}>
            <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 14px",
              background: "rgba(34, 197, 94, 0.10)",
              border: "1px solid rgba(34, 197, 94, 0.35)",
              borderRadius: 6,
              color: "#166534",
              fontSize: 13,
              lineHeight: 1.55,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <CheckCircle2 size={16} />
            <div>
              {pick(
                "パスワードを変更しました。他のデバイスはサインアウトされました。",
                "Password updated. Other devices have been signed out.",
              )}
            </div>
          </div>
        )}

        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setCurrentPassword("");
              setNewPassword("");
              setNewPasswordConfirm("");
              setError(null);
              setSuccess(false);
            }}
            disabled={submitting}
          >
            {pick("クリア", "Clear")}
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {submitting
              ? pick("変更中...", "Changing...")
              : pick("パスワードを変更", "Change Password")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
